import { useEffect, useRef, useState, useCallback } from "react";
import {
  Wand2, Scissors, Languages, Download, FileText,
  ChevronLeft, ChevronRight, Pause, Play,
} from "lucide-react";

// Each slide owns its accent — the outer panel picks up the accent-* class
// so the same component switches colour + gradient + icon-halo per slide.
// Keeping the classes fully-spelled means Tailwind's JIT doesn't drop them.
const SLIDES = [
  {
    id: "auto",
    icon: Wand2,
    kicker: "01 · Ingest",
    title: "Upload &",
    titleAccent: "Auto-Transcribe",
    line: "Paste a video, get subtitles in seconds. No manual typing.",
    grad: "from-blue-500 via-blue-400 to-cyan-300",
    ring: "shadow-[0_0_120px_rgba(59,130,246,0.35)]",
    text: "text-blue-300",
    bg: "bg-blue-500",
    dotOn: "bg-blue-400",
  },
  {
    id: "edit",
    icon: Scissors,
    kicker: "02 · Refine",
    title: "Edit with",
    titleAccent: "Timeline Control",
    line: "Click any segment, edit text, adjust timing visually.",
    grad: "from-fuchsia-500 via-purple-500 to-indigo-400",
    ring: "shadow-[0_0_120px_rgba(217,70,239,0.35)]",
    text: "text-fuchsia-300",
    bg: "bg-fuchsia-500",
    dotOn: "bg-fuchsia-400",
  },
  {
    id: "indian",
    icon: Languages,
    kicker: "03 · Localise",
    title: "Indian Language",
    titleAccent: "Support",
    line: "Works with Hindi, regional languages, and colloquial speech.",
    grad: "from-orange-500 via-amber-400 to-yellow-300",
    ring: "shadow-[0_0_120px_rgba(249,115,22,0.35)]",
    text: "text-orange-300",
    bg: "bg-orange-500",
    dotOn: "bg-orange-400",
  },
  {
    id: "export",
    icon: Download,
    kicker: "04 · Deliver",
    title: "Export Ready",
    titleAccent: "Subtitles",
    line: "Download in SRT or VTT format, ready to upload anywhere.",
    grad: "from-emerald-500 via-teal-400 to-cyan-300",
    ring: "shadow-[0_0_120px_rgba(16,185,129,0.35)]",
    text: "text-emerald-300",
    bg: "bg-emerald-500",
    dotOn: "bg-emerald-400",
  },
  {
    id: "import",
    icon: FileText,
    kicker: "05 · Bring your own",
    title: "Import Existing",
    titleAccent: "Subtitles",
    line: "Already have a subtitle file? Upload and edit directly.",
    grad: "from-rose-500 via-pink-500 to-red-400",
    ring: "shadow-[0_0_120px_rgba(244,63,94,0.35)]",
    text: "text-rose-300",
    bg: "bg-rose-500",
    dotOn: "bg-rose-400",
  },
];

const AUTO_MS = 5000;

export default function FeatureSlideshow() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [dir, setDir] = useState(1); // +1 forward, -1 back — animation direction
  const timerRef = useRef(null);

  const go = useCallback((next) => {
    setDir(next > i ? 1 : -1);
    // Wrap around cleanly at both edges.
    setI(((next % SLIDES.length) + SLIDES.length) % SLIDES.length);
  }, [i]);

  const advance = useCallback(() => go(i + 1), [go, i]);
  const back = useCallback(() => go(i - 1), [go, i]);

  useEffect(() => {
    if (paused) return;
    timerRef.current = setTimeout(advance, AUTO_MS);
    return () => clearTimeout(timerRef.current);
  }, [i, paused, advance]);

  // ← / → keyboard shortcuts when the slideshow section is in the viewport.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); advance(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); back(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [advance, back]);

  const slide = SLIDES[i];
  const Icon = slide.icon;

  return (
    <section
      data-testid="feature-slideshow"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative w-full py-16 md:py-24 border-y border-zinc-900 overflow-hidden bg-zinc-950"
    >
      {/* Ambient accent glow — re-tints per slide */}
      <div
        aria-hidden
        className={`absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full opacity-20 blur-3xl transition-colors duration-700 bg-gradient-to-br ${slide.grad}`}
      />
      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center gap-3 mb-8 md:mb-12">
          <div className={`text-[10px] font-mono uppercase tracking-[0.35em] ${slide.text} transition-colors duration-500`}>
            Feature tour
          </div>
          <div className="h-px flex-1 bg-zinc-800" />
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            {String(i + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-center min-h-[360px] md:min-h-[420px]">
          {/* Icon column */}
          <div className="md:col-span-4 flex justify-center md:justify-start">
            <div
              key={`icon-${slide.id}`}
              data-testid={`slide-icon-${slide.id}`}
              className={`relative w-40 h-40 md:w-56 md:h-56 bg-gradient-to-br ${slide.grad} ${slide.ring} flex items-center justify-center transition-all duration-700 animate-fade-slide-in`}
              style={{ borderRadius: "22% 78% 30% 70% / 70% 20% 80% 30%" }}
            >
              <Icon className="w-20 h-20 md:w-28 md:h-28 text-white drop-shadow-2xl" strokeWidth={1.6} />
            </div>
          </div>

          {/* Text column */}
          <div className="md:col-span-8 relative">
            <div
              key={`text-${slide.id}`}
              className="animate-fade-slide-in"
              style={{ animationDelay: "80ms" }}
              data-testid={`slide-text-${slide.id}`}
            >
              <div className={`text-[11px] font-mono uppercase tracking-[0.4em] mb-4 ${slide.text} transition-colors duration-500`}>
                {slide.kicker}
              </div>
              <h2 className="font-black tracking-tighter leading-[0.95] text-white text-5xl sm:text-6xl md:text-7xl lg:text-[6rem]">
                {slide.title}
                <br />
                <span className={`bg-clip-text text-transparent bg-gradient-to-r ${slide.grad}`}>
                  {slide.titleAccent}
                </span>
              </h2>
              <p className="mt-6 text-lg md:text-xl text-zinc-300 max-w-xl leading-relaxed">
                {slide.line}
              </p>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="mt-12 md:mt-16 flex items-center gap-4 md:gap-6">
          <button
            type="button"
            data-testid="slideshow-prev"
            onClick={back}
            className="w-12 h-12 border border-zinc-800 hover:border-white text-white/70 hover:text-white grid place-items-center transition-all"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            data-testid="slideshow-next"
            onClick={advance}
            className="w-12 h-12 border border-zinc-800 hover:border-white text-white/70 hover:text-white grid place-items-center transition-all"
            aria-label="Next slide"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <button
            type="button"
            data-testid="slideshow-pause"
            onClick={() => setPaused((p) => !p)}
            className="hidden md:inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-white h-12 px-3 border border-zinc-800 hover:border-white transition-all"
            aria-label={paused ? "Resume auto-advance" : "Pause auto-advance"}
          >
            {paused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
            {paused ? "Play" : "Pause"}
          </button>

          {/* Dots — clickable + they double as a progress bar */}
          <div className="ml-auto flex items-center gap-3">
            {SLIDES.map((s, idx) => {
              const active = idx === i;
              return (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`slideshow-dot-${idx + 1}`}
                  onClick={() => go(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                  className={`group relative overflow-hidden transition-all duration-500 ${active ? `h-2 w-12 ${s.dotOn}` : "h-2 w-2 bg-zinc-700 hover:bg-zinc-500"}`}
                >
                  {active && !paused && (
                    <span
                      key={`fill-${i}`}
                      className="absolute inset-y-0 left-0 bg-white/40 animate-slideshow-fill"
                      style={{ animationDuration: `${AUTO_MS}ms` }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Component-scoped animations — kept inline so we don't have to touch
          the global CSS file for a single feature. */}
      <style>{`
        @keyframes fade-slide-in {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-slide-in {
          animation: fade-slide-in 550ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        @keyframes slideshow-fill {
          from { width: 0%; }
          to   { width: 100%; }
        }
        .animate-slideshow-fill { animation: slideshow-fill linear both; }
      `}</style>
    </section>
  );
}
